import { useCallback, useEffect, useState } from "react";

/** Three RTT tiers when the request succeeds; `error` is reserved for real failures. */
export type PingConnectionStatus = "loading" | "success" | "moderate" | "slow" | "error";

export interface PingResult {
  url: string;
  responseTime: number | null;
  status: PingConnectionStatus;
  lastPing: Date | null;
}

export const DEFAULT_PING_INTERVAL_MS = 60_000;
/** Boundaries between tier 1 (best) → 2 → 3; above tier 3 is still `slow`, not `error`. */
const LATENCY_TIER1_MS = 400;
const LATENCY_TIER2_MS = 1000;
const TIMEOUT_MS = 10_000;

const getStatusFromResponseTime = (responseTime: number): PingConnectionStatus => {
  if (responseTime < LATENCY_TIER1_MS) return "success";
  if (responseTime < LATENCY_TIER2_MS) return "moderate";
  return "slow";
};

const createWebSocketPing = (url: string, startTime: number, protocol?: string) => {
  return new Promise<{ responseTime: number; status: PingConnectionStatus }>((resolve) => {
    const ws = new WebSocket(url, protocol);
    let resolved = false;

    const cleanup = () => {
      if (!resolved) {
        resolved = true;
        ws.close();
      }
    };

    const timeout = setTimeout(() => {
      cleanup();
      resolve({ responseTime: TIMEOUT_MS, status: "error" });
    }, TIMEOUT_MS);

    const resolveSuccess = () => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        const responseTime = performance.now() - startTime;
        ws.close();
        resolve({
          responseTime,
          status: getStatusFromResponseTime(responseTime),
        });
      }
    };

    const resolveError = () => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        const responseTime = performance.now() - startTime;
        resolve({ responseTime, status: "error" });
      }
    };

    ws.onopen = () => {
      // For regular WebSocket, connection is enough
      resolveSuccess();
    };

    ws.onerror = resolveError;
  });
};

const createHttpPing = async (
  url: string,
  startTime: number,
  options: {
    method?: string;
    headers?: Record<string, string>;
    body?: string;
  } = {},
) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: options.method || "HEAD",
      signal: controller.signal,
      headers: options.headers,
      body: options.body,
    });

    clearTimeout(timeoutId);
    const responseTime = performance.now() - startTime;

    return {
      responseTime,
      status: response.ok ? getStatusFromResponseTime(responseTime) : ("error" as const),
    };
  } catch (_error) {
    clearTimeout(timeoutId);
    const responseTime = performance.now() - startTime;
    return { responseTime, status: "error" as const };
  }
};

export type UsePingOptions = {
  /** Defaults to {@link DEFAULT_PING_INTERVAL_MS}. */
  intervalMs?: number;
};

export const usePing = (urls: string[], options?: UsePingOptions) => {
  const intervalMs = options?.intervalMs ?? DEFAULT_PING_INTERVAL_MS;
  const [results, setResults] = useState<PingResult[]>(
    urls.map((url) => ({
      url,
      responseTime: null,
      status: "loading",
      lastPing: null,
    })),
  );

  const pingUrl = useCallback(async (url: string): Promise<{ responseTime: number; status: PingConnectionStatus }> => {
    const startTime = performance.now();

    try {
      // Same resource as EkidenClient.system.getSystemInfo() — lightweight public GET.
      if (url.includes("/api/")) {
        return await createHttpPing(`${url}/info`, startTime, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
      }

      // Regular WebSocket
      if (url.startsWith("ws")) {
        return await createWebSocketPing(url, startTime);
      }

      // Fallback HTTP
      return await createHttpPing(url, startTime);
    } catch (_error) {
      const responseTime = performance.now() - startTime;
      return { responseTime, status: "error" };
    }
  }, []);

  const performPing = useCallback(async () => {
    const newResults = await Promise.all(
      urls.map(async (url) => {
        const { responseTime, status } = await pingUrl(url);
        return {
          url,
          responseTime,
          status,
          lastPing: new Date(),
        };
      }),
    );

    setResults(newResults);
  }, [urls, pingUrl]);

  useEffect(() => {
    if (urls.length === 0) return;

    performPing();

    const interval = setInterval(performPing, intervalMs);

    return () => clearInterval(interval);
  }, [performPing, urls, intervalMs]);

  return { results, performPing };
};
